import { Card } from "../Components/Card";
import { useEffect, useRef } from "react";
import { InformationCircleIcon } from "@heroicons/react/20/solid";
import { Tooltip } from "react-tooltip";

import primeClient from "../axios/primeClient";
import hiClient from "../axios/hiClient";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PieChart } from "../Components/PieChart";
import { Colors } from "../constants/Colors";

// Mona Hi device status
const onlineColor = Colors.alertsgreen100;
const offlineColor = Colors.alertsred100;
const technicalIssueColor = "#a7a9ac";
const suspendColor = Colors.alertsyellow100;

// Mona Prime device status
const clearColor = onlineColor;
const attentionColor = suspendColor;
const downColor = offlineColor;

export const PieChartCard = () => {
  const chartContainerRef = useRef(null);
  const [deviceData, setDeviceData] = useState(null);
  const [chartOption, setChartOption] = useState(null);
  const loginType = localStorage.getItem("login_type");
  const isMobile = window.innerWidth <= 570;
  const { t } = useTranslation();

  useEffect(() => {
    const fetchChartData = async () => {
      if (!loginType) return;

      let data = {};
      try {
        const internetCountResponse =
          loginType === "prime"
            ? await primeClient.get(`/v1/apps/count`)
            : await hiClient.get(`/mobile/internet/count`);

        data = internetCountResponse.data;
        setDeviceData(data);
      } catch (error) {
        console.log(error);
        return;
      }

      console.log("count",data);

      const chartData =
        loginType === "prime"
          ? [
              {
                value: data.devices_clear,
                name: "Device Clear",
                itemStyle: { color: clearColor },
              },
              {
                value: data.devices_down,
                name: "Device Down",
                itemStyle: { color: downColor },
              },
              {
                value: data.devices_attention,
                name: "Device Attention",
                itemStyle: { color: attentionColor },
              },
            ]
          : [
              {
                value: data.online,
                name: "Internet Online",
                itemStyle: { color: onlineColor },
              },
              {
                value: data.offline,
                name: "Internet Offline",
                itemStyle: { color: offlineColor },
              },
              {
                value: data.unknown,
                name: "Internet Technical Issue",
                itemStyle: { color: technicalIssueColor },
              },
              {
                value: data.suspend,
                name: "Internet Suspend",
                itemStyle: { color: suspendColor },
              },
            ];

      const option = {
        legend: {
          show: false,
        },
        series: [
          {
            name: "Internet Status",
            type: "pie",
            radius: ["60%", "80%"],
            avoidLabelOverlap: true,
            padAngle: 3,
            label: {
              show: true,
              position: "center",
              formatter: `{a|${
                loginType === "prime" ? data.total_devices : data.total
              }}\n{b|${t("prime.deviceCondition.totalDevices")}}`,
              rich: {
                a: {
                  fontSize: 24,
                  fontWeight: "bold",
                  color: "#333",
                },
                b: {
                  fontSize: 12,
                  color: "#444",
                },
              },
            },
            labelLine: {
              show: false,
            },
            data: chartData,
          },
        ],
      };
      setChartOption(option);
    };
    fetchChartData();
  }, [chartContainerRef]);

  return (
    <Card
      style={{
        width: "100%",
        height: isMobile ? "100%" : "100%",
        minHeight: "12.5rem",
        flexGrow: "1",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "1rem",
        gap: "1rem",
      }}
    >
      {chartOption ? (
        <PieChart
          chartWidth={isMobile ? 150 : 200}
          chartHeight={isMobile ? 150 : 200}
          options={chartOption}
          style={{
            width: "40%",
            padding: 0,
            height: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            flexGrow: "1",
          }}
        />
      ) : (
        <div
          style={{
            width: "40%",
            padding: 0,
            height: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            flexGrow: "1",
          }}
        >
          Loading...
        </div>
      )}
      {loginType === "prime" ? (
        <div
          style={{
            width: isMobile ? "50%" : "70%",
            minWidth: "140px",
            height: "100%",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
            gap: "1rem",
          }}
        >
          <StatusDescriptionSubCard
            title={"Clear"}
            color={clearColor}
            amount={deviceData?.devices_clear ?? 0}
            loginType={loginType}
          />
          <StatusDescriptionSubCard
            title={"Attention"}
            color={attentionColor}
            amount={deviceData?.devices_attention ?? 0}
            loginType={loginType}
          />
          <StatusDescriptionSubCard
            title={"Down"}
            color={downColor}
            amount={deviceData?.devices_down ?? 0}
            loginType={loginType}
          />
        </div>
      ) : (
        <div
          style={{
            width: isMobile ? "60%" : "70%",
            height: "100%",
            display: "grid",
            gridTemplateColumns: isMobile
              ? "repeat(auto-fill, minmax(110px, 1fr))"
              : "repeat(2, 1fr)",
            gridTemplateRows: "repeat(2, 1fr)",
            gap: "1rem",
          }}
        >
          <StatusDescriptionSubCard
            title={"Online"}
            color={onlineColor}
            amount={deviceData?.online ?? 0}
            loginType={loginType}
          />
          <StatusDescriptionSubCard
            title={"Offline"}
            color={offlineColor}
            amount={deviceData?.offline ?? 0}
            loginType={loginType}
          />
          <StatusDescriptionSubCard
            title={"Technical Issue"}
            color={technicalIssueColor}
            amount={deviceData?.unknown ?? 0}
            loginType={loginType}
          />
          <StatusDescriptionSubCard
            title={"Suspend"}
            color={suspendColor}
            amount={deviceData?.suspend ?? 0}
            loginType={loginType}
          />
        </div>
      )}
    </Card>
  );
};

const StatusDescriptionSubCard = ({ title, color, amount, loginType }) => {
  const isMobile = window.innerWidth <= 570;

  const { t } = useTranslation();

  // prettier-ignore
  const tooltipContent = {
    "Online": t("hi.deviceCondition.desc.online"),
    "Offline": t("hi.deviceCondition.desc.offline"),
    "Technical Issue": t("hi.deviceCondition.desc.techIssue"),
    "Suspend": t("hi.deviceCondition.desc.suspend"),
    "Clear": t("prime.deviceCondition.desc.clear"),
    "Attention": t("prime.deviceCondition.desc.attention"),
    "Down": t("prime.deviceCondition.desc.down"),
  }

  return (
    <div
      style={{
        width: isMobile ? "90%" : "100%",
        height: "100%",
        backgroundColor: Colors.neutral5,
        borderRadius: "5px",
        display: "flex",
        alignItems: "center",
        padding: "0.5rem",
        paddingLeft: "1rem",
        gap: "1rem",
      }}
    >
      <div>
        <Circle color={color} />
      </div>
      <div style={{ fontWeight: "bold" }}>
        <div
          style={{
            fontSize: !isMobile
              ? loginType === "prime"
                ? "2.5rem"
                : "1.25rem"
              : loginType === "prime"
              ? "1.5rem"
              : "1rem",

            color: "#3e3e3e",
            maxWidth: "100%",
            overflowWrap: "anywhere",
          }}
        >
          {amount}
        </div>
        <div
          style={{
            fontSize: isMobile
              ? loginType === "prime"
                ? "0.9rem"
                : "0.8rem"
              : loginType === "prime"
              ? "1rem"
              : "0.9rem",
            color: "#747373",
            overflowWrap: "anywhere",
          }}
        >
          {title}{" "}
          <InformationCircleIcon
            width={"1rem"}
            style={{ display: "inline", cursor: "pointer" }}
            data-tooltip-id={`${title}-tooltip`}
          />
          <Tooltip
            id={`${title}-tooltip`}
            place="bottom"
            content={tooltipContent[title]}
            style={{ fontSize: "0.7rem", maxWidth: "200px" }}
          />
        </div>
      </div>
    </div>
  );
};

const Circle = ({ color }) => {
  return (
    <div
      style={{
        backgroundColor: color,
        width: "2rem",
        height: "2rem",
        borderRadius: "50%",
      }}
    ></div>
  );
};
